import {
  MenuCategory,
  DiningTable,
  QRTemplate,
  RestaurantProfile,
  Order,
  HeroSettings,
  HeroSlide,
  OfferBanner,
  LegalPagesSettings,
  Coupon,
  ThemeCustomization,
  StaffUser,
  EventBooking
} from '../types';

export const initialRestaurantProfile: RestaurantProfile = {
  name: 'The Oven Story',
  domain: 'theovenstory.fi',
  address: 'Aleksanterinkatu 18',
  city: 'Helsinki',
  postal_code: '00170',
  business_id: 'FI3149821-4',
  phone: '+358 40 555 8920',
  email: 'asiakaspalvelu@theovenstory.fi',
  opening_hours_fi: 'Ma–To 11:00–22:00 | Pe 11:00–23:00 | La 12:00–23:00 | Su 12:00–21:00',
  opening_hours_en: 'Mon–Thu 11:00–22:00 | Fri 11:00–23:00 | Sat 12:00–23:00 | Sun 12:00–21:00',
  compliance_text_fi: 'EU GDPR & Suomen ALV-lainsäädännön mukainen',
  compliance_text_en: 'EU GDPR & Finnish Tax Law Compliant',
  copyright_text: '© 2026 The Oven Story (theovenstory.fi). Kaikki oikeudet pidätetään.',
  food_vat_rate: 14.0,
  beverage_vat_rate: 25.5
};

export const initialThemeCustomization: ThemeCustomization = {
  primary_color: '#E05A36', // Scandinavian Warm Terracotta / Copper
  accent_style: 'copper'
};

export const initialCoupons: Coupon[] = [
  {
    id: 'cpn_lunch',
    code: 'LUNCH1490',
    title: 'Lounas Alennus (Lunch Discount)',
    description: 'Säästä 3,50 € tilauksestasi arkilounaalla (ma–pe klo 11–14).',
    discount_type: 'fixed',
    discount_value: 3.50,
    min_order_amount: 15.00,
    valid_days: [1, 2, 3, 4, 5], // Mon to Fri
    valid_start_time: '11:00',
    valid_end_time: '15:00',
    customer_type: 'all',
    festival_tag: 'Lounas / Lunch',
    is_active: true
  },
  {
    id: 'cpn_welcome10',
    code: 'OVEN10',
    title: 'Tervetuliaisalennus 10% (Welcome 10%)',
    description: '10% alennus yli 25 € pöytätilauksiin.',
    discount_type: 'percentage',
    discount_value: 10,
    min_order_amount: 25.00,
    max_discount_amount: 15.00,
    valid_days: [0, 1, 2, 3, 4, 5, 6],
    customer_type: 'all',
    festival_tag: 'Yleinen / General',
    is_active: true
  },
  {
    id: 'cpn_weekend',
    code: 'WEEKEND5',
    title: 'Viikonloppu Herkuttelu 5 €',
    description: '5 € alennus la–su yli 35 € illallisiin.',
    discount_type: 'fixed',
    discount_value: 5.00,
    min_order_amount: 35.00,
    valid_days: [0, 6], // Sat & Sun
    customer_type: 'all',
    festival_tag: 'Viikonloppu',
    is_active: true
  }
];

export const initialOfferBanners: OfferBanner[] = [
  {
    id: 'offer_lunch',
    badge: 'LUNCH SPECIAL 🍕',
    title_fi: 'Lounasaikaan klo 11–14: Valinnainen pizza + Lähdevesi / Kahvi vain 14,90 €',
    title_en: 'Lunch Special 11–14: Any Artisan Pizza + Spring Water / Coffee only 14.90 €',
    title_sv: 'Luncherbjudande 11–14: Valfri pizza + Källvatten / Kaffe endast 14.90 €',
    subtitle_fi: 'Voimassa ma–pe pöytätilauksiin. Syötä koodi LUNCH1490 kassalla.',
    subtitle_en: 'Valid Mon–Fri for table orders. Use code LUNCH1490 at checkout.',
    subtitle_sv: 'Gäller mån–fre för bordsbeställningar. Ange kod LUNCH1490 i kassan.',
    discount_code: 'LUNCH1490',
    bg_gradient: 'linear-gradient(135deg, #e05a36, #b91c1c)',
    display_mode: 'minimal_ribbon',
    is_active: true
  }
];

export const initialHeroSlides: HeroSlide[] = [
  {
    id: 'slide_brand',
    slide_type: 'brand_hero',
    badge_text: '🔥 NORDIC ARTISAN KITCHEN',
    title_fi: 'Aito Puu-Uunipizzeria & Pohjoismainen Keittiö',
    title_en: 'Artisan Wood-Fired Pizzeria & Nordic Kitchen',
    title_sv: 'Äkta Vedugnspizzeria & Nordiskt Kök',
    subtitle_fi: 'Tilaa suoraan pöydästäsi ilman jonoja. 48h hapanjuuripizzat & puhtaat raaka-aineet.',
    subtitle_en: 'Order directly to your table with no queues. Authentic 48h sourdough pizzas & fresh Nordic ingredients.',
    subtitle_sv: 'Beställ direkt till bordet utan köer. Äkta surdegspizzor och färska nordiska råvaror.',
    image_url: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1400&q=80',
    tags: [
      { icon: '🔥', text_fi: '450°C Puu-uuni', text_en: '450°C Wood Oven', text_sv: '450°C Vedugn' },
      { icon: '🇫🇮', text_fi: 'Suomalaiset raaka-aineet', text_en: 'Finnish Ingredients', text_sv: 'Finska råvaror' },
      { icon: '🌾', text_fi: '48h Hapanjuuri', text_en: '48h Sourdough', text_sv: '48h Surdeg' }
    ],
    is_active: true,
    sort_order: 1
  },
  {
    id: 'slide_lunch',
    slide_type: 'promo_offer',
    badge_text: '🎁 LOUNASTARJOUS 11–14',
    title_fi: 'Lounasaikaan klo 11–14: Valinnainen pizza + Juoma vain 14,90 €',
    title_en: 'Lunch Special 11–14: Any Artisan Pizza + Spring Water only 14.90 €',
    title_sv: 'Luncherbjudande 11–14: Valfri pizza + Dryck endast 14.90 €',
    subtitle_fi: 'Voimassa ma–pe pöytätilauksiin. Syötä koodi LUNCH1490 kassalla.',
    subtitle_en: 'Valid Mon–Fri for table orders. Use code LUNCH1490 at checkout.',
    subtitle_sv: 'Gäller mån–fre för bordsbeställningar. Ange kod LUNCH1490 i kassan.',
    discount_code: 'LUNCH1490',
    bg_gradient: 'linear-gradient(135deg, rgba(224, 90, 54, 0.92), rgba(185, 28, 28, 0.9))',
    image_url: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1200&q=80',
    cta_label_fi: 'Käytä koodi LUNCH1490',
    cta_label_en: 'Apply Voucher: LUNCH1490',
    cta_label_sv: 'Använd kod LUNCH1490',
    is_active: true,
    sort_order: 2
  },
  {
    id: 'slide_reindeer',
    slide_type: 'dish_highlight',
    badge_text: '⭐ CHEF SPECIALTY',
    title_fi: 'Nordic Reindeer & Lapland Porcini Truffle Pizza',
    title_en: 'Nordic Reindeer & Lapland Porcini Truffle Pizza',
    title_sv: 'Nordisk Ren & Lappland Karljohan Tryffelpizza',
    subtitle_fi: 'Savustettua Lapin poroa, villitatti-tryffelikreemiä ja aitoa Fior di Lattea.',
    subtitle_en: 'Smoked Lapland reindeer, wild porcini truffle cream & Fior di Latte mozzarella.',
    subtitle_sv: 'Rökt Lapplandsren, vild Karljohan-tryffelkräm & Fior di Latte mozzarella.',
    bg_gradient: 'linear-gradient(135deg, rgba(26, 32, 44, 0.92), rgba(45, 55, 72, 0.9))',
    image_url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=1200&q=80',
    is_active: true,
    sort_order: 3
  }
];

export const initialLegalSettings: LegalPagesSettings = {
  privacy_policy_fi: `Tietosuojaseloste (EU GDPR & Suomen Tietosuojalaki 1050/2018)

1. Rekisterinpitäjä:
The Oven Story (Y-tunnus: FI3149821-4)
Aleksanterinkatu 18, 00170 Helsinki, Suomi
Sähköposti: asiakaspalvelu@theovenstory.fi

2. Kerättävät tiedot ja käsittelyn tarkoitus:
Keräämme asiakkaan nimen ja puhelinnumeron ainoastaan pöytätilauksen toimittamista, keittiön käsittelyä ja tilauksen etenemisestä ilmoittamista varten (WhatsApp/SMS). Tietoja ei luovuteta kolmansille osapuolille suoramarkkinointiin.

3. Tietojen säilytysaika ja oikeudet:
Tiedot säilytetään ainoastaan asiointitapahtuman ja kirjanpitolain edellyttämän ajan. Asiakkaalla on oikeus pyytää tietojensa tarkastamista tai poistamista rekisteristä.`,

  privacy_policy_en: `Privacy Policy (EU GDPR & Finnish Data Protection Act 1050/2018)

1. Data Controller:
The Oven Story (Business ID: FI3149821-4)
Aleksanterinkatu 18, 00170 Helsinki, Finland
Email: asiakaspalvelu@theovenstory.fi

2. Data Collected & Purpose of Processing:
We collect customer name and WhatsApp/phone number exclusively to fulfill table orders, kitchen preparation, and send real-time order status updates. Your contact data is never shared with third parties or used for unsolicited marketing.

3. Data Retention & Customer Rights:
Data is retained strictly for the duration of dining service and required accounting periods. Customers maintain full rights to request inspection, modification, or erasure of their personal information.`,

  terms_of_service_fi: `Tilaus- ja Käyttöehdot (Kuluttajansuojalaki & Ravintolapalvelut)

1. Tilauksen tekeminen ja vahvistus:
Pöydästä tehty tilaus siirtyy suoraan keittiöömme. Asiakas voi vapaasti perua tai muokata tilaustaan ennen keittiön vahvistusta. Vahvistuksen jälkeen muutospyynnöt käsitellään vuoropäällikön hyväksynnällä.

2. Hinnat ja arvonlisävero:
Kaikki hinnat sisältävät Suomen lakisääteisen arvonlisäveron (Ruoka-annokset ALV 14%, Juomat ALV 25.5%).

3. Maksaminen:
Lasku maksetaan pöytään tai kassalle aterioinnin päätteeksi kortilla tai käteisellä.`,

  terms_of_service_en: `Terms of Ordering & Service (Finnish Consumer Protection)

1. Order Placement & Kitchen Confirmation:
Orders placed via table QR code are transmitted directly to the kitchen queue. Customers may cancel or modify items before staff confirmation. Once confirmed, change requests require manager approval to ensure kitchen workflow integrity.

2. Pricing & Value Added Tax (VAT / ALV):
All listed prices are in Euros (€) and include applicable Finnish Value Added Tax (Food 14% ALV, Beverages 25.5% ALV).

3. Payment:
Orders can be paid at table service or payment counter upon completion of meal via debit/credit card or cash.`,

  allergen_policy_fi: `Allergeeni-ilmoitus & Oiva-raportointi (Ruokavirasto)

1. Erityisruokavaliot & Merkinnät:
Annoksemme on merkitty selkeästi pohjoismaisten standardien mukaisesti:
• G = Gluteeniton
• L = Laktoositon
• VL = Vähälaktoosinen
• M = Maidoton
• VEG = Vegaaninen

2. Ristiallergiat & Keittiöohjeet:
Valmistamme tuotteita keittiössä, jossa käsitellään vehnäjauhoja, pähkinöitä, maitotuotteita ja kalaa. Kysy henkilökunnalta lisätietoja mahdollisista vakavista allergioista.

3. Oiva-valvonta:
Toimintaamme valvoo Helsingin kaupungin ympäristöpalvelut (Ruokaviraston Oiva-järjestelmä).`,

  allergen_policy_en: `Allergen Disclosure & Food Safety (Finnish Food Authority / Ruokavirasto)

1. Dietary Classifications:
All dishes are itemized according to Nordic food standards:
• G = Gluten-Free
• L = Lactose-Free
• VL = Low Lactose
• M = Dairy-Free
• VEG = Vegan

2. Cross-Contamination Warning:
Our wood-fired kitchen handles wheat flour, dairy, nuts, and seafood. Please notify our staff via the special instructions box regarding any severe allergies.

3. Food Safety Inspection (Oiva):
Our restaurant operates under full inspection compliance with the City of Helsinki Environmental Services and Finnish Food Authority standards.`
};

export const initialHeroSettings: HeroSettings = {
  title_fi: 'Aito puu-uunipizzeria & pohjoismainen keittiö',
  title_en: 'Artisan Wood-Fired Pizzeria & Nordic Kitchen',
  title_sv: 'Vedugnspizzeria & Nordiskt kök',
  desc_fi: 'Tilaa suoraan pöydästäsi ilman jonotusta. Aito puu-uunipizza, tuoreet kotimaiset raaka-aineet ja nopea palvelu.',
  desc_en: 'Order directly to your table with no queues. Authentic wood-fired sourdough pizzas & fresh Nordic ingredients.',
  desc_sv: 'Beställ direkt till bordet utan kö. Äkta vedugnspizzor och färska nordiska råvaror.',
  cover_image_url: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1200&q=80',
  tags: [
    { icon: '🔥', text_fi: '450°C Puu-uuni', text_en: '450°C Wood Oven', text_sv: '450°C Vedugn' },
    { icon: '🇫🇮', text_fi: 'Kotimaiset raaka-aineet', text_en: 'Finnish Ingredients', text_sv: 'Finska råvaror' },
    { icon: '🌾', text_fi: '48h Hapanjuuri', text_en: '48h Sourdough', text_sv: '48h Surdeg' }
  ]
};

export const initialCategories: MenuCategory[] = [
  {
    id: 'cat_pizzas',
    name_fi: 'Puu-uunipizzat',
    name_en: 'Wood-Fired Pizzas',
    name_sv: 'Vedugnspizzor',
    description: '48h hapatettu artesaanipohja, paistettu 450°C kiviuunissa',
    icon: '🍕',
    sort_order: 1,
    is_active: true,
    items: [
      {
        id: 'pizza_poro_truffle',
        category_id: 'cat_pizzas',
        name_fi: 'Pohjoinen Poro & Tryffeli',
        name_en: 'Nordic Reindeer & Truffle',
        name_sv: 'Nordisk Ren & Tryffel',
        description_fi: 'Savustettua Lapin poroa, villitatti-tryffelikreemiä, Fior di Latte -mozzarellaa ja tuoretta puolukkaa.',
        description_en: 'Smoked Lapland reindeer, wild porcini truffle cream, Fior di Latte mozzarella and fresh lingonberries.',
        description_sv: 'Rökt ren från Lappland, Karljohan-tryffelkräm, Fior di Latte mozzarella och färska lingon.',
        price: 21.50,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['L'],
        sort_order: 1,
        variants: [
          {
            id: 'crust',
            title: { fi: 'Pohjavalinta', en: 'Crust Choice', sv: 'Val av botten' },
            required: true,
            options: [
              { id: 'classic_sourdough', name: { fi: 'Klassinen hapanjuuripohja', en: 'Classic 48h Sourdough', sv: 'Klassisk surdegsbotten' }, price: 0 },
              { id: 'gluten_free_crust', name: { fi: 'Gluteeniton pohja (+2,50 €)', en: 'Gluten-Free Base (+2.50 €)', sv: 'Glutenfri botten (+2.50 €)' }, price: 2.50 }
            ]
          },
          {
            id: 'extras',
            title: { fi: 'Lisätäytteet', en: 'Extra Toppings', sv: 'Extra pålägg' },
            required: false,
            options: [
              { id: 'extra_truffle_oil', name: { fi: 'Extra Valkotryffeliöljy', en: 'Extra White Truffle Oil', sv: 'Extra vit tryffelolja' }, price: 1.50 },
              { id: 'extra_fior_di_latte', name: { fi: 'Lisää Fior di Latte mozzarellaa', en: 'Extra Mozzarella', sv: 'Extra mozzarella' }, price: 2.00 }
            ]
          }
        ]
      },
      {
        id: 'pizza_margherita_dop',
        category_id: 'cat_pizzas',
        name_fi: 'Margherita San Marzano D.O.P',
        name_en: 'Margherita San Marzano D.O.P',
        name_sv: 'Margherita San Marzano D.O.P',
        description_fi: 'Makeaa San Marzano -tomaattikastiketta, aitoa puhvelinmozzarellaa, tuoretta basilikaa ja neitsytoliiviöljyä.',
        description_en: 'Sweet San Marzano tomato sauce, authentic Buffalo mozzarella, fresh basil, and extra virgin olive oil.',
        description_sv: 'Söt San Marzano tomatsås, äkta buffelmozzarella, färsk basilika och extra virgin olivolja.',
        price: 15.90,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1604382355076-af4b0eb60143?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: false,
        spice_level: 0,
        dietary_flags: ['L', 'VL'],
        sort_order: 2,
        variants: [
          {
            id: 'crust',
            title: { fi: 'Pohjavalinta', en: 'Crust Choice', sv: 'Val av botten' },
            required: true,
            options: [
              { id: 'classic_sourdough', name: { fi: 'Klassinen hapanjuuripohja', en: 'Classic 48h Sourdough', sv: 'Klassisk surdegsbotten' }, price: 0 },
              { id: 'gluten_free_crust', name: { fi: 'Gluteeniton pohja (+2,50 €)', en: 'Gluten-Free Base (+2.50 €)', sv: 'Glutenfri botten (+2.50 €)' }, price: 2.50 }
            ]
          }
        ]
      },
      {
        id: 'pizza_kantarelli_veg',
        category_id: 'cat_pizzas',
        name_fi: 'Metsäkantarelli & Timjami (Vegaaninen)',
        name_en: 'Wild Chanterelle & Thyme (Vegan)',
        name_sv: 'Vild Kantarell & Timjan (Vegansk)',
        description_fi: 'Voissa paistettuja suomalaisia kantarelleja, kaurapohjaista valkokreemiä, karamellisoitua sipulia ja tuoretta timjamia.',
        description_en: 'Finnish wild chanterelles, oat-based white creme, caramelized red onions and fresh organic thyme.',
        description_sv: 'Finska vilda kantareller, havrebaserad vitkräm, karamelliserad rödlök och färsk timjan.',
        price: 18.50,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['VEG', 'M', 'L'],
        sort_order: 3,
        variants: [
          {
            id: 'crust',
            title: { fi: 'Pohjavalinta', en: 'Crust Choice', sv: 'Val av botten' },
            required: true,
            options: [
              { id: 'classic_sourdough', name: { fi: 'Klassinen hapanjuuripohja', en: 'Classic 48h Sourdough', sv: 'Klassisk surdegsbotten' }, price: 0 },
              { id: 'gluten_free_crust', name: { fi: 'Gluteeniton pohja (+2,50 €)', en: 'Gluten-Free Base (+2.50 €)', sv: 'Glutenfri botten (+2.50 €)' }, price: 2.50 }
            ]
          }
        ]
      },
      {
        id: 'pizza_garden_vegan',
        category_id: 'cat_pizzas',
        name_fi: 'Artesaani Puutarha & Artisokka (Vegaaninen)',
        name_en: 'Artisan Garden Truffle & Artichoke (Vegan)',
        name_sv: 'Trädgård & Kronärtskocka (Vegansk)',
        description_fi: 'Puu-uunissa paahdettua artisokkaa, vegaanista mozzarellaa, valkosipuliöljyä, pinjansiemeniä ja rucolaa.',
        description_en: 'Wood-roasted globe artichokes, artisan vegan mozzarella, garlic herb oil, pine nuts and fresh baby arugula.',
        description_sv: 'Vedugnsrostad kronärtskocka, vegansk mozzarella, vitlöksolja och ruccola.',
        price: 17.90,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1593560708920-61dd98c46a4e?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['VEG', 'M', 'L'],
        sort_order: 4,
        variants: [
          {
            id: 'crust',
            title: { fi: 'Pohjavalinta', en: 'Crust Choice', sv: 'Val av botten' },
            required: true,
            options: [
              { id: 'classic_sourdough', name: { fi: 'Klassinen hapanjuuripohja', en: 'Classic 48h Sourdough', sv: 'Klassisk surdegsbotten' }, price: 0 },
              { id: 'gluten_free_crust', name: { fi: 'Gluteeniton pohja (+2,50 €)', en: 'Gluten-Free Base (+2.50 €)', sv: 'Glutenfri botten (+2.50 €)' }, price: 2.50 }
            ]
          }
        ]
      },
      {
        id: 'pizza_diavola_spicy',
        category_id: 'cat_pizzas',
        name_fi: 'Diavola Nduja & Calabrian Chili',
        name_en: 'Diavola Nduja & Calabrian Chili',
        name_sv: 'Diavola Nduja & Kalabrisk Chili',
        description_fi: 'Mausteista calabrialaista Ndujaa, Spianata salamia, hunajaglaseerausta ja paahdettua chiliä.',
        description_en: 'Spicy Calabrian Nduja sausage, Spianata salami, hot honey drizzle and roasted chili flakes.',
        description_sv: 'Kryddig kalabrisk Nduja korv, Spianata salami, het honung och rostade chiliflarn.',
        price: 18.90,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1628840042765-356cda07504e?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: false,
        spice_level: 2,
        dietary_flags: ['L'],
        sort_order: 5,
        variants: [
          {
            id: 'crust',
            title: { fi: 'Pohjavalinta', en: 'Crust Choice', sv: 'Val av botten' },
            required: true,
            options: [
              { id: 'classic_sourdough', name: { fi: 'Klassinen hapanjuuripohja', en: 'Classic 48h Sourdough', sv: 'Klassisk surdegsbotten' }, price: 0 },
              { id: 'gluten_free_crust', name: { fi: 'Gluteeniton pohja (+2,50 €)', en: 'Gluten-Free Base (+2.50 €)', sv: 'Glutenfri botten (+2.50 €)' }, price: 2.50 }
            ]
          }
        ]
      },
      {
        id: 'pizza_quattro_formaggi',
        category_id: 'cat_pizzas',
        name_fi: 'Neljä Juustoa & Lapin Hunaja',
        name_en: 'Quattro Formaggi & Lapland Honey',
        name_sv: 'Fyra Ostar & Lapplandshonung',
        description_fi: 'Gorgonzolaa, kypsytettyä parmesaania, fontinaa, Fior di Lattea ja luomuhunajaa.',
        description_en: 'Creamy Gorgonzola, aged Parmesan, Alpine Fontina, Fior di Latte and organic wildflower honey.',
        description_sv: 'Krämig Gorgonzola, lagrad parmesan, Fontina och ekologisk honung.',
        price: 17.50,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1573821663912-569905455b1c?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['VEG', 'L', 'VL'],
        sort_order: 6,
        variants: [
          {
            id: 'crust',
            title: { fi: 'Pohjavalinta', en: 'Crust Choice', sv: 'Val av botten' },
            required: true,
            options: [
              { id: 'classic_sourdough', name: { fi: 'Klassinen hapanjuuripohja', en: 'Classic 48h Sourdough', sv: 'Klassisk surdegsbotten' }, price: 0 },
              { id: 'gluten_free_crust', name: { fi: 'Gluteeniton pohja (+2,50 €)', en: 'Gluten-Free Base (+2.50 €)', sv: 'Glutenfri botten (+2.50 €)' }, price: 2.50 }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'cat_starters',
    name_fi: 'Alkupalat & Keitot',
    name_en: 'Starters & Soups',
    name_sv: 'Förrätter & Soppor',
    description: 'Pohjoismaiset raaka-aineet ja tuoreet maut',
    icon: '🥣',
    sort_order: 2,
    is_active: true,
    items: [
      {
        id: 'soup_lohikeitto',
        category_id: 'cat_starters',
        name_fi: 'Perinteinen Kermainen Lohikeitto (Gluteeniton)',
        name_en: 'Traditional Creamy Salmon Soup (Gluten-Free)',
        name_sv: 'Traditionell Krämig Laxsoppa (Glutenfri)',
        description_fi: 'Tuoretta suomalaista merilohta, voissa haudutettua perunaa, runsaasti tilliä ja saaristolaisleipää.',
        description_en: 'Fresh Finnish salmon, butter-braised new potatoes, abundant fresh dill and house-baked archipelago bread.',
        description_sv: 'Färsk finsk lax, smörbräserad potatis, rikligt med dill och hembakat skärgårdsbröd.',
        price: 16.50,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['G', 'L'],
        sort_order: 1
      },
      {
        id: 'starter_burrata',
        category_id: 'cat_starters',
        name_fi: 'Savustettu Burrata & Tyrnikreemi',
        name_en: 'Smoked Burrata & Sea Buckthorn',
        name_sv: 'Rökt Burrata & Havtornskräm',
        description_fi: 'Kokonainen kermainen burrata, uunissa paahdettuja kirsikkatomaatteja, kotimaista tyrnihilloketta ja focacciaa.',
        description_en: 'Creamy artisanal burrata, wood-roasted cherry tomatoes, Finnish sea buckthorn compote and warm focaccia.',
        description_sv: 'Krämig hantverksburrata, ugnsrostade körsbärstomater, havtornskompott och varm focaccia.',
        price: 13.90,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1592417817098-8f3d6910985b?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['VEG', 'L', 'VL'],
        sort_order: 2
      },
      {
        id: 'salad_beetroot_dairyfree',
        category_id: 'cat_starters',
        name_fi: 'Paahdettu Punajuuri-Pähkinäsalaatti (Maidoton / M)',
        name_en: 'Nordic Roasted Beetroot & Walnut Salad (Dairy-Free / M)',
        name_sv: 'Rostad Rödbeta & Valnötssallad (Mjölkfri)',
        description_fi: 'Uunissa karamellisoituja kotimaisia punajuuria, paahdettuja saksanpähkinöitä, vadelmavinegrettiä ja salaattia.',
        description_en: 'Slow-roasted Finnish beets, toasted walnuts, raspberry vinaigrette and fresh crisp organic greens.',
        description_sv: 'Ugnsrostade rödbetor, rostade valnötter och hallonvinaigrette.',
        price: 14.50,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: false,
        spice_level: 0,
        dietary_flags: ['M', 'VEG', 'G', 'L'],
        sort_order: 3
      }
    ]
  },
  {
    id: 'cat_pastas',
    name_fi: 'Tuorepastat & Pääruoat',
    name_en: 'Fresh Pastas & Mains',
    name_sv: 'Färsk Pasta & Varmrätter',
    description: 'Päivittäin keittiössämme käsintehty tuorepasta ja puu-uuniannokset',
    icon: '🍝',
    sort_order: 3,
    is_active: true,
    items: [
      {
        id: 'pasta_truffle_tagliatelle',
        category_id: 'cat_pastas',
        name_fi: 'Tryffeli-Parmesaanipasta',
        name_en: 'Truffle & Aged Parmesan Tagliatelle',
        name_sv: 'Tryffel & Parmesan Tagliatelle',
        description_fi: 'Käsin leikattua tagliatelle-tuorepastaa, mustatryffelikreemiä, 24kk kypsytettyä parmesaania ja paahdettuja pinjansiemeniä.',
        description_en: 'Hand-cut fresh tagliatelle, black truffle cream emulsion, 24-month aged Parmigiano Reggiano and toasted pine nuts.',
        description_sv: 'Färsk handskuren tagliatelle, svart tryffelkräm, 24 månaders parmesan och rostade pinjenötter.',
        price: 19.50,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1621996346565-e3d5d6281699?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['L'],
        sort_order: 1
      },
      {
        id: 'risotto_truffle_gf',
        category_id: 'cat_pastas',
        name_fi: 'Gluteeniton Metsätattirisotto',
        name_en: 'Gluten-Free Forest Porcini Risotto',
        name_sv: 'Glutenfri Karljohanrisotto',
        description_fi: 'Kermainen Carnaroli-risotto, kuullotettuja herkkutatteja, parmesaania ja valkotryffeliöljyä.',
        description_en: 'Creamy Carnaroli arborio risotto, sautéed wild porcini mushrooms, aged parmesan and white truffle essence.',
        description_sv: 'Krämig Carnarolirisotto med Karljohansvamp och vit tryffelolja.',
        price: 19.90,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1633964913295-ceb43826e7c9?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG'],
        sort_order: 2
      },
      {
        id: 'ribs_bbq_dairyfree',
        category_id: 'cat_pastas',
        name_fi: 'Savustetut BBQ-Porsaankyljykset (Maidoton / M)',
        name_en: 'Slow-Smoked Nordic BBQ Ribs (Dairy-Free / M)',
        name_sv: 'Rökta BBQ-Revbensspjäll (Mjölkfri)',
        description_fi: 'Puu-uunissa 6h mureaksi haudutetut porsaanribsit, puolukka-BBQ-glaseeraus ja rosmariiniperunat.',
        description_en: '6-hour wood-oven tender smoked pork ribs with tart lingonberry BBQ glaze and rosemary crushed potatoes.',
        description_sv: 'Möra rökta revbensspjäll med lingon-BBQ-glaze och rosmarinpotatis.',
        price: 22.90,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: false,
        spice_level: 0,
        dietary_flags: ['M', 'G', 'L'],
        sort_order: 3
      }
    ]
  },
  {
    id: 'cat_desserts',
    name_fi: 'Jälkiruoat',
    name_en: 'Desserts',
    name_sv: 'Efterrätter',
    description: 'Makea päätös puu-uuni-illalliselle',
    icon: '🍨',
    sort_order: 4,
    is_active: true,
    items: [
      {
        id: 'dessert_leipajuusto',
        category_id: 'cat_desserts',
        name_fi: 'Lämmin Leipäjuusto & Lakkahillo',
        name_en: 'Warm Lapland Bread Cheese & Cloudberry',
        name_sv: 'Varm Kaffeost & Hjortronsylt',
        description_fi: 'Puu-uunissa pehmeäksi paistettua kainuulaista leipäjuustoa, kermavaahtoa ja aitoa Lapin lakkahilloa.',
        description_en: 'Wood-oven baked traditional northern cheese bathed in cream, served with golden wild cloudberries.',
        description_sv: 'Ugnsbakad kaffeost med grädde och guldgula vilda hjortron.',
        price: 9.80,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        spice_level: 0,
        dietary_flags: ['G', 'L'],
        sort_order: 1
      },
      {
        id: 'dessert_tiramisu',
        category_id: 'cat_desserts',
        name_fi: 'The Oven Story Tiramisù',
        name_en: 'The Oven Story Artisan Tiramisù',
        name_sv: 'The Oven Story Tiramisù',
        description_fi: 'Espressoon ja Amarettoon kostutetut savoiardi-keksit, kuohkea mascarponekreemi ja Valrhona-kaakaojauhe.',
        description_en: 'Italian Savoiardi ladyfingers soaked in fresh espresso and Amaretto, whipped mascarpone cream and Valrhona dark cocoa.',
        description_sv: 'Savoiardikex dränkta i espresso och Amaretto, fluffig mascarponekräm och Valrhonakakao.',
        price: 8.90,
        vat_rate: 14.0,
        image_url: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: false,
        spice_level: 0,
        dietary_flags: ['L'],
        sort_order: 2
      }
    ]
  },
  {
    id: 'cat_alcohol',
    name_fi: 'Viinit & Pienpanimo-oluet (18+)',
    name_en: 'Artisan Wines & Craft Beers (18+)',
    name_sv: 'Vin & Hantverksöl (18+)',
    description: 'Kotimaiset pienpanimo-oluet, toscanalaiset viinit ja klassinen Lonkero',
    icon: '🍷',
    sort_order: 5,
    is_active: true,
    items: [
      {
        id: 'bev_craft_ipa',
        category_id: 'cat_alcohol',
        name_fi: 'Fat Lizard Ankle Breaker IPA 5.2% (0.44L)',
        name_en: 'Fat Lizard Finnish Craft IPA 5.2% (0.44L)',
        name_sv: 'Fat Lizard Finsk Hantverks-IPA 5.2% (0.44L)',
        description_fi: 'Espoossa pantu raikas, trooppisen humaloitu ja mehukas suomalainen käsityöläisolut.',
        description_en: 'Locally brewed in Espoo Finland, juicy and tropical American style IPA.',
        description_sv: 'Lokalt bryggd i Esbo Finland, fruktig och tropiskt humlad IPA.',
        price: 9.20,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1608270119330-d3e911246c4f?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        is_alcohol: true,
        alcohol_abv: 5.2,
        spice_level: 0,
        dietary_flags: ['L', 'VEG'],
        sort_order: 1
      },
      {
        id: 'bev_lonkero',
        category_id: 'cat_alcohol',
        name_fi: 'Hartwall Original Long Drink 5.5% (0.33L)',
        name_en: 'Finnish "Lonkero" Original Gin Long Drink 5.5% (0.33L)',
        name_sv: 'Original Gin Long Drink "Lonkero" 5.5% (0.33L)',
        description_fi: 'Helsingin vuoden 1952 olympialaisiin luotu aito ja ikoninen gini-greippijuoma.',
        description_en: 'The legendary Finnish gin & grapefruit long drink, crafted since the 1952 Helsinki Olympics.',
        description_sv: 'Klassisk finsk gin och grapefruktdryck från OS i Helsingfors 1952.',
        price: 8.50,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1556881286-fc6915169721?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: false,
        is_alcohol: true,
        alcohol_abv: 5.5,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG'],
        sort_order: 2
      },
      {
        id: 'bev_sandels',
        category_id: 'cat_alcohol',
        name_fi: 'Sandels Premium Finnish Lager 4.7% (0.5L)',
        name_en: 'Sandels Premium Finnish Draught Lager 4.7% (0.5L)',
        name_sv: 'Sandels Premium Finsk Lager 4.7% (0.5L)',
        description_fi: 'Pehmeä, raikas ja kullankeltainen suomalainen premium-lagerolut hanasta.',
        description_en: 'Smooth and crisp golden Finnish draught lager, perfect refreshing companion.',
        description_sv: 'Len och frisk gyllengul finsk fatöl.',
        price: 7.90,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1535958636474-b021ee887b13?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: false,
        is_alcohol: true,
        alcohol_abv: 4.7,
        spice_level: 0,
        dietary_flags: ['L', 'VEG'],
        sort_order: 3
      },
      {
        id: 'bev_chianti',
        category_id: 'cat_alcohol',
        name_fi: 'Chianti Classico DOCG Punaviini (16cl / 75cl)',
        name_en: 'Chianti Classico DOCG Tuscan Red Wine (16cl / 75cl)',
        name_sv: 'Chianti Classico DOCG Rödvin (16cl / 75cl)',
        description_fi: 'Täyteläinen toscanalainen punaviini, jossa kypsiä kirsikoita ja mausteisuutta. Täydellinen puu-uunipizzan pariin.',
        description_en: 'Rich Tuscan Sangiovese red wine with dark cherry notes. The perfect pairing for wood-fired artisan pizza.',
        description_sv: 'Fylligt toskanskt rödvin med inslag av mörka körsbär.',
        price: 11.50,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        is_alcohol: true,
        alcohol_abv: 13.5,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG'],
        sort_order: 4,
        variants: [
          {
            id: 'size',
            title: { fi: 'Koko / Size', en: 'Serving Size', sv: 'Storlek' },
            required: true,
            options: [
              { id: 'glass_16cl', name: { fi: 'Lasi (16cl)', en: 'Glass (16cl)', sv: 'Glas (16cl)' }, price: 0 },
              { id: 'bottle_75cl', name: { fi: 'Pullo (75cl) (+36,50 €)', en: 'Full Bottle 75cl (+36.50 €)', sv: 'Flaska 75cl (+36.50 €)' }, price: 36.50 }
            ]
          }
        ]
      },
      {
        id: 'bev_pinot_grigio',
        category_id: 'cat_alcohol',
        name_fi: 'Pinot Grigio delle Venezie Valkoviini (16cl / 75cl)',
        name_en: 'Pinot Grigio delle Venezie White Wine (16cl / 75cl)',
        name_sv: 'Pinot Grigio delle Venezie Vitt Vin (16cl / 75cl)',
        description_fi: 'Raikas, kuiva ja hedelmäinen pohjoisitalialainen valkoviini. Sopii erinomaisesti lohikeitolle ja burratalle.',
        description_en: 'Crisp, dry, and floral Northern Italian white wine. Excellent with creamy salmon soup & burrata.',
        description_sv: 'Friskt och torrt norditalienskt vitt vin.',
        price: 10.50,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1584916201218-f4242ceb4809?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: true,
        is_alcohol: true,
        alcohol_abv: 12.5,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG'],
        sort_order: 5,
        variants: [
          {
            id: 'size',
            title: { fi: 'Koko / Size', en: 'Serving Size', sv: 'Storlek' },
            required: true,
            options: [
              { id: 'glass_16cl', name: { fi: 'Lasi (16cl)', en: 'Glass (16cl)', sv: 'Glas (16cl)' }, price: 0 },
              { id: 'bottle_75cl', name: { fi: 'Pullo (75cl) (+33,50 €)', en: 'Full Bottle 75cl (+33.50 €)', sv: 'Flaska 75cl (+33.50 €)' }, price: 33.50 }
            ]
          }
        ]
      },
      {
        id: 'bev_prosecco',
        category_id: 'cat_alcohol',
        name_fi: 'Prosecco Millesimato DOC Spumante (12cl / 75cl)',
        name_en: 'Prosecco Millesimato DOC Sparkling Wine (12cl / 75cl)',
        name_sv: 'Prosecco Millesimato DOC Mousserande (12cl / 75cl)',
        description_fi: 'Hedelmäinen ja elegantti italialainen kuohuviini hienoilla kuplilla.',
        description_en: 'Crisp and celebratory Italian sparkling wine with fine delicate bubbles.',
        description_sv: 'Fruktigt och elegant italienskt mousserande vin.',
        price: 9.50,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1560512823-829485b8bf24?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        is_alcohol: true,
        alcohol_abv: 11.0,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG'],
        sort_order: 6,
        variants: [
          {
            id: 'size',
            title: { fi: 'Koko / Size', en: 'Serving Size', sv: 'Storlek' },
            required: true,
            options: [
              { id: 'glass_12cl', name: { fi: 'Lasi (12cl)', en: 'Glass (12cl)', sv: 'Glas (12cl)' }, price: 0 },
              { id: 'bottle_75cl', name: { fi: 'Pullo (75cl) (+32,50 €)', en: 'Full Bottle 75cl (+32.50 €)', sv: 'Flaska 75cl (+32.50 €)' }, price: 32.50 }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'cat_soft_drinks',
    name_fi: 'Mocktailit & Lähdevedet & Kahvit',
    name_en: 'Mocktails, Spring Waters & Coffees',
    name_sv: 'Alkoholfria Drinkar, Källvatten & Kaffe',
    description: 'Pohjoismaiset alkoholittomat mocktailit, Salpausselän lähdevesi ja artesaanikahvit',
    icon: '🥤',
    sort_order: 6,
    is_active: true,
    items: [
      {
        id: 'bev_lingonberry_spritz',
        category_id: 'cat_soft_drinks',
        name_fi: 'Helsinki Puolukka-Kuplavesi (Mocktail)',
        name_en: 'Helsinki Wild Lingonberry Spritz (Mocktail)',
        name_sv: 'Helsingfors Lingon Spritz (Alkoholfri)',
        description_fi: 'Käsin poimittua suomalaista puolukkaa, rosmariinisiirappia, sitruunaa ja raikasta hiilihapotettua lähdevettä.',
        description_en: 'Hand-picked Finnish lingonberries, house rosemary syrup, organic lemon and sparkling spring water.',
        description_sv: 'Finska handplockade lingon, rosmarinsirap, citron och kolsyrat källvatten.',
        price: 6.50,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        is_alcohol: false,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG', 'M'],
        sort_order: 1
      },
      {
        id: 'bev_cloudberry_spritz',
        category_id: 'cat_soft_drinks',
        name_fi: 'Lapin Lakkaspritz (Mocktail)',
        name_en: 'Arctic Cloudberry Spritz (Mocktail)',
        name_sv: 'Hjortron Spritz (Alkoholfri)',
        description_fi: 'Aitoa Lapin villilakkaa, inkiväärilimonadia, tuoretta minttua ja lähdevettä.',
        description_en: 'Golden wild Lapland cloudberries, artisanal ginger lemonade, fresh mint and chilled spring water.',
        description_sv: 'Vilda hjortron från Lappland, ingefärslemonad och färsk mynta.',
        price: 6.90,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1551024709-8f23befc6f87?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: true,
        is_recommended: true,
        is_alcohol: false,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG', 'M'],
        sort_order: 2
      },
      {
        id: 'bev_spring_water',
        category_id: 'cat_soft_drinks',
        name_fi: 'Suomalainen Luonnon Lähdevesi (0.5L)',
        name_en: 'Finnish Still Spring Water Bottle (0.5L)',
        name_sv: 'Finskt Stilla Källvatten (0.5L)',
        description_fi: 'Raikasta Salpausselän harjujen suodattamaa luonnonlähdevettä.',
        description_en: 'Crystal clear natural artesian spring water from Finnish ridges.',
        description_sv: 'Rent finskt naturligt källvatten från Salpausselkä.',
        price: 3.50,
        vat_rate: 25.5,
        image_url: 'https://images.unsplash.com/photo-1548839140-29a749e1bc4e?auto=format&fit=crop&w=800&q=80',
        is_available: true,
        is_popular: false,
        is_recommended: false,
        is_alcohol: false,
        spice_level: 0,
        dietary_flags: ['G', 'L', 'VEG', 'M'],
        sort_order: 3
      }
    ]
  }
];

export const initialDiningTables: DiningTable[] = [
  { id: 'tbl_1', table_number: 1, label: 'Pöytä 1', capacity: 2, zone: 'Window Bay', is_active: true },
  { id: 'tbl_2', table_number: 2, label: 'Pöytä 2', capacity: 2, zone: 'Window Bay', is_active: true },
  { id: 'tbl_3', table_number: 3, label: 'Pöytä 3', capacity: 4, zone: 'Main Dining', is_active: true },
  { id: 'tbl_4', table_number: 4, label: 'Pöytä 4', capacity: 4, zone: 'Main Dining', is_active: true },
  { id: 'tbl_5', table_number: 5, label: 'Pöytä 5', capacity: 6, zone: 'Hearth Lounge', is_active: true },
  { id: 'tbl_6', table_number: 6, label: 'Pöytä 6', capacity: 6, zone: 'Hearth Lounge', is_active: true },
  { id: 'tbl_7', table_number: 7, label: 'Pöytä 7', capacity: 8, zone: 'VIP Lounge', is_active: true },
  { id: 'tbl_8', table_number: 8, label: 'Pöytä 8', capacity: 4, zone: 'Summer Terrace', is_active: true }
];

export const initialQRTemplate: QRTemplate = {
  id: 'tmpl_ovenstory',
  brand_title: 'The Oven Story',
  tagline_fi: 'Skannaa & Tilaa Pöytääsi',
  tagline_en: 'Scan & Order to Your Table',
  tagline_sv: 'Skanna & Beställ till Bordet',
  primary_color: '#E05A36',
  background_color: '#FFFFFF',
  footer_text: 'theovenstory.fi • Helsinki',
  include_wifi: true,
  wifi_name: 'TheOvenStory_Guest',
  wifi_password: 'artisanpizzafi'
};

export const initialSampleOrders: Order[] = [];

export const initialStaffUsers: StaffUser[] = [
  {
    id: 'usr_admin_master',
    name: 'Devarsh Soni (Admin)',
    email: 'devarshsoni@hotmail.com',
    mobile: '+358 40 123 4567',
    password: 'Adm!nDev@',
    pin_code: '0101',
    role: 'admin',
    is_active: true,
    created_at: '2026-08-28T09:00:00.000Z'
  },
  {
    id: 'usr_kitchen_1',
    name: 'Pääkokki / Head Chef',
    email: 'kitchen@theovenstory.fi',
    mobile: '+358 40 555 1122',
    password: 'Kitchen!2024',
    pin_code: '2024',
    role: 'kitchen',
    is_active: true,
    created_at: '2026-08-28T09:00:00.000Z'
  },
  {
    id: 'usr_manager_1',
    name: 'Vuoropäällikkö / Shift Manager',
    email: 'manager@theovenstory.fi',
    mobile: '+358 40 777 3344',
    password: 'Manager!5588',
    pin_code: '5588',
    role: 'manager',
    is_active: true,
    created_at: '2026-08-28T09:00:00.000Z'
  }
];

export const initialEventBookings: EventBooking[] = [
  {
    id: 'evt_101',
    fullName: 'Laura Korhonen',
    email: 'laura.korhonen@teknologia.fi',
    phone: '+358 40 123 9988',
    eventType: 'corporate',
    guests: 24,
    preferredDate: '2026-11-20',
    message: 'Haluaisimme varata kabinetin pikkujouluihin. Puu-uunipizzat ja viinipaketit toiveena.',
    status: 'pending',
    staffNotes: '',
    submittedAt: '2026-10-06T14:30:00.000Z'
  },
  {
    id: 'evt_102',
    fullName: 'Mikael Lindström',
    email: 'mikael.lindstrom@nordicgroup.se',
    phone: '+358 50 882 1144',
    eventType: 'birthday',
    guests: 18,
    preferredDate: '2026-10-24',
    message: '40-vuotissyntymäpäiväillallinen. Muutama erikoisruokavalio (gluteeniton & maidoton).',
    status: 'contacted',
    staffNotes: 'Soitettu asiakkaalle 07.10. Menuvaihtoehdot lähetetty sähköpostitse.',
    submittedAt: '2026-10-05T10:15:00.000Z'
  },
  {
    id: 'evt_103',
    fullName: 'Sofia Virtanen',
    email: 'sofia.virtanen@designhelsinki.fi',
    phone: '+358 44 991 3322',
    eventType: 'buyout',
    guests: 65,
    preferredDate: '2026-12-12',
    message: 'Koko ravintolan vuokraus firman vuoden loppujuhliin klo 18–23. Baarin ja keittiön räätälöity tarjoilu.',
    status: 'confirmed',
    staffNotes: 'Sopimus allekirjoitettu. Ennakkomaksu 50% vastaanotettu.',
    submittedAt: '2026-10-01T16:00:00.000Z'
  }
];

